'use client';

import * as React from 'react';
import { cn } from '../../lib/cn';

/**
 * FileViewer — the render target for a deep-agent `show_file` semantic tool call.
 * The agent surfaces a local working-folder file (a report it just wrote, a
 * fetched doc…) as a clean viewer card instead of pasting the raw text into the
 * chat. Sits inline in the assistant turn (S2 full column or S3 docked panel).
 *
 * Chrome: header (file glyph · title · muted path · ext/line badge · copy ·
 * collapse chevron) + a scrollable body. The body renders `children` when the
 * consumer pre-renders it (e.g. markdown for a .md report); otherwise it falls
 * back to a mono <pre> of `content`. `content` always drives copy + line count.
 *
 * Token-only; reuses the card + `--file-*` families. `mode="auto"` adapts full↔panel
 * via a @container query; motion is neutralized globally by base.css reduced-motion.
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Ico({
  size = 15,
  className,
  children,
}: {
  size?: number;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...STROKE}
      className={cn('block flex-none', className)}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const FileGlyph = (
  <>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
  </>
);
const CopyGlyph = (
  <>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </>
);
const CheckGlyph = <path d="M20 6 9 17l-5-5" />;
const ChevronGlyph = <path d="m6 9 6 6 6-6" />;
const FolderGlyph = (
  <>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    <path d="m9 13 2 2 4-4" />
  </>
);

/** Last path segment ("/a/b/report.md" → "report.md"). */
function baseName(path: string): string {
  const clean = String(path || '').replace(/\/+$/, '');
  const seg = clean.split('/');
  return seg[seg.length - 1] || clean || '파일';
}

/** File extension, lowercased, without the dot ("report.md" → "md"). */
function extName(path: string): string {
  const base = baseName(path);
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

function lineCount(text: string): number {
  if (!text) return 0;
  const n = text.split('\n').length;
  // Trailing newline shouldn't count as an extra empty line.
  return text.endsWith('\n') ? n - 1 : n;
}

export interface FileViewerProps {
  /** Root-relative file path (e.g. "/report.md"). Shown muted; used for name/ext. */
  path: string;
  /** Card title. Defaults to the file's base name. */
  title?: string;
  /** Raw file text — drives copy, line count, and the default mono body. */
  content: string;
  /**
   * Pre-rendered body (e.g. markdown). When provided it replaces the default
   * mono <pre>; `content` is still used for copy + the line-count badge.
   */
  children?: React.ReactNode;
  /**
   * Reveal the file in the OS file explorer (Finder/Explorer). Only wired in the
   * desktop app (the file is a real local file); when omitted the button hides.
   */
  onReveal?: () => void;
  /** Start expanded. Default `true` (the agent called show_file to show it). */
  defaultOpen?: boolean;
  /** `full` · `panel` (narrow) · `auto` (@container-adaptive). Default `auto`. */
  mode?: 'full' | 'panel' | 'auto';
  className?: string;
}

export function FileViewer({
  path,
  title,
  content,
  children,
  onReveal,
  defaultOpen = true,
  mode = 'auto',
  className,
}: FileViewerProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const copy = React.useCallback(() => {
    void navigator.clipboard?.writeText(content).then(() => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    });
  }, [content]);

  const name = title?.trim() || baseName(path);
  const ext = extName(path);
  const lines = lineCount(content);
  // Show the path as a subtitle only when it adds info beyond the title.
  const showPath = Boolean(path) && path !== name;

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col overflow-hidden font-sans text-fg',
        'rounded-[var(--file-radius)] border border-border bg-surface shadow-[var(--shadow-card)]',
        mode === 'auto' && '@container',
        className,
      )}
    >
      {/* header */}
      <div className="flex items-center gap-[var(--space-2-5)] border-b border-border-subtle bg-[var(--color-bg-surface-2)] px-[var(--space-3-5)] py-[var(--space-2-5)]">
        <span className="flex size-[26px] flex-none items-center justify-center rounded-[var(--radius-sm)] bg-accent-soft">
          <Ico size={15} className="text-accent">
            {FileGlyph}
          </Ico>
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-fg">{name}</span>
          {showPath ? (
            <span className="truncate font-mono text-2xs text-fg-muted">{path}</span>
          ) : null}
        </div>

        {/* meta badge: ext + line count (hidden on very narrow panels) */}
        <span className="flex flex-none items-center gap-[var(--space-1-5)] @max-[300px]:hidden">
          {ext ? (
            <span className="rounded-full bg-muted px-[var(--space-2)] py-0.5 font-mono text-2xs uppercase text-fg-muted">
              {ext}
            </span>
          ) : null}
          {lines > 0 ? (
            <span className="whitespace-nowrap font-mono text-2xs text-fg-muted">{lines}줄</span>
          ) : null}
        </span>

        {onReveal ? (
          <button
            type="button"
            onClick={onReveal}
            aria-label="파일 위치 열기"
            title="파일 탐색기에서 위치 보기"
            className={cn(
              'flex flex-none cursor-pointer items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border-none bg-transparent',
              'px-[var(--space-1-5)] py-[var(--space-1)] text-2xs font-medium',
              'text-fg-muted transition-colors hover:bg-muted hover:text-fg-secondary',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus-ring)]',
            )}
          >
            <Ico size={13}>{FolderGlyph}</Ico>
            <span className="@max-[420px]:hidden">폴더에서 보기</span>
          </button>
        ) : null}

        <button
          type="button"
          onClick={copy}
          aria-label="파일 내용 복사"
          className={cn(
            'flex flex-none cursor-pointer items-center gap-[var(--space-1)] rounded-[var(--radius-sm)] border-none bg-transparent',
            'px-[var(--space-1-5)] py-[var(--space-1)] text-2xs font-medium',
            'text-fg-muted transition-colors hover:bg-muted hover:text-fg-secondary',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus-ring)]',
          )}
        >
          <Ico size={13} className={copied ? 'text-[var(--color-success)]' : undefined}>
            {copied ? CheckGlyph : CopyGlyph}
          </Ico>
          <span className="@max-[360px]:hidden">{copied ? '복사됨' : '복사'}</span>
        </button>

        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? '접기' : '펼치기'}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'flex flex-none cursor-pointer items-center rounded-[var(--radius-sm)] border-none bg-transparent p-[var(--space-1)]',
            'text-fg-muted transition-colors hover:bg-muted hover:text-fg-secondary',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-focus-ring)]',
          )}
        >
          <Ico
            size={16}
            className={cn(
              'transition-transform duration-[var(--duration-base)] ease-[var(--ease-out)]',
              open && 'rotate-180',
            )}
          >
            {ChevronGlyph}
          </Ico>
        </button>
      </div>

      {/* body */}
      {open ? (
        <div
          role="region"
          aria-label={name}
          className={cn(
            'max-h-[var(--file-body-max-h)] overflow-auto',
            'animate-[lb-status-in_var(--duration-base)_var(--ease-out)]',
          )}
        >
          {children != null ? (
            <div className="px-[var(--space-4)] py-[var(--space-3-5)] text-base leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              {children}
            </div>
          ) : (
            <pre className="m-0 whitespace-pre-wrap px-[var(--space-4)] py-[var(--space-3-5)] font-mono text-xs leading-[var(--leading-normal)] text-fg-secondary [overflow-wrap:anywhere]">
              {content}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}
